import { useEffect, useRef } from "react";

/**
 * Prefetch a route chunk (or warm any async loader) the first time the element
 * enters the viewport. Complements usePrefetchOnIntent — that fires on hover
 * for desktop pointers; this catches mobile scroll where there is no hover.
 *
 * Safety:
 *  - Fires at most once per mount (firedRef guards re-entry).
 *  - Honors the data-saver hint (`navigator.connection.saveData`) and the
 *    `prefers-reduced-data` media query so we don't burn metered bandwidth.
 *  - No-op when IntersectionObserver is missing (very old browsers).
 */
export function usePrefetchOnVisible<T extends HTMLElement>(
  loader: () => Promise<unknown> | void,
  options?: { rootMargin?: string; threshold?: number },
) {
  const ref = useRef<T | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    const el = ref.current;
    if (!el || typeof window === "undefined") return;
    if (typeof IntersectionObserver === "undefined") return;

    // Respect data-saver preferences.
    const conn = (navigator as any).connection;
    if (conn?.saveData) return;
    if (window.matchMedia?.("(prefers-reduced-data: reduce)").matches) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !firedRef.current) {
            firedRef.current = true;
            try {
              const r = loader();
              if (r && typeof (r as Promise<unknown>).catch === "function") {
                (r as Promise<unknown>).catch(() => {
                  firedRef.current = false;
                });
              }
            } catch {
              firedRef.current = false;
            }
            io.disconnect();
            break;
          }
        }
      },
      {
        rootMargin: options?.rootMargin ?? "200px",
        threshold: options?.threshold ?? 0,
      },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loader, options?.rootMargin, options?.threshold]);

  return ref;
}
